/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { bind } from 'bind-event-listener';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { useAnalyticsFlow } from '../../../analytics/analytics-flow/analyticsFlowUtils';
import {
	isPartialCopy,
	isSelectionWithinElement,
} from '../../../experience-application/screens-with-logic/utils/copy-response';

type PreviewRendererProps = {
	jsonDocument: DocNode;
	showTelepointer: boolean;
	providerFactory: ProviderFactory;
	reduceMargins?: boolean;
};

export const PreviewRenderer = ({
	jsonDocument,
	showTelepointer,
	providerFactory,
	reduceMargins,
}: PreviewRendererProps) => {
	const analyticsFlow = useAnalyticsFlow();

	const previewRendererRef = useRef<HTMLDivElement>(null);
	const copiedOnceRef = useRef(false);

	// Ignored via go/ees005
	// eslint-disable-next-line require-await
	const onCopyHandler = useCallback(async () => {
		const previewRendererElement = previewRendererRef.current;
		if (!analyticsFlow || !previewRendererElement) {
			return;
		}

		if (!isSelectionWithinElement(previewRendererElement)) {
			return;
		}

		if (isPartialCopy(previewRendererRef.current)) {
			analyticsFlow.addAttributes({
				contentCopiedPartially: true,
			});
		}

		// exit if copied already for this preview
		if (copiedOnceRef.current) {
			return;
		}

		analyticsFlow.addAttributes({
			contentCopied: true,
		});
		copiedOnceRef.current = true;
	}, [analyticsFlow]);

	useEffect(() => {
		const unbind = bind(document, {
			type: 'copy',
			listener: onCopyHandler,
		});
		return unbind;
	}, [onCopyHandler, analyticsFlow]);

	// override renderer's syntax highlighting styles so it appears consistent with the editor
	const styles = [
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.code-block .token.token': {
				fontStyle: 'normal',
				fontWeight: token('font.weight.regular'),
				color: token('color.text', N800),
			},
		}),
	];

	const reduceMarginStyles = css({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.ak-renderer-document >:first-child': {
			marginTop: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.ak-renderer-document >:last-child': {
			marginBottom: 0,
		},
	});

	if (reduceMargins) {
		styles.push(reduceMarginStyles);
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
		<div data-testid="preview-renderer" css={styles} ref={previewRendererRef}>
			<RendererActionsContext>
				<ReactRenderer
					document={jsonDocument}
					media={{
						// We don't currently support images with captions
						allowLinking: false,
					}}
					// By default -- the renderer uses a "default schema" which supports all
					// the nodes and marks the markdown transformer can create.
					// see packages/editor/adf-schema/src/schema/default-schema.ts for details.
					useSpecBasedValidator
					addTelepointer={showTelepointer}
					dataProviders={providerFactory}
					allowPlaceholderText
				/>
			</RendererActionsContext>
		</div>
	);
};
